import { getIn } from 'final-form';
import mime from 'mime-types';
import { createSelector } from 'reselect';

import { TireSeasonEnum } from 'components/interventions/detail/Works/components/TireReport/constants';
import { HEALTH_STATUS } from 'constants/Global';
import { MAX_DEFAULT_MESSAGE_LENGTH } from 'constants/InterventionConstants';
import { LEGACY_MEDIA_TYPE } from 'constants/MediaConstants';
import { THEME_LABEL } from 'modules/app/constants';
import { humanize, ucFirst } from 'utils/global';
import { uniq } from 'utils/ObjectUtils';

const OVERRIDE_EXTENSIONS = {
  GLOBAL: {
    'application/octet-stream': ['dat', 'evt', 'gsr', 'pddata', 'psdata', 'rem'],
    'application/vnd.ms-excel': [mime.extension('application/vnd.ms-excel')],
    'application/vnd.ms-powerpoint': [mime.extension('application/vnd.ms-excel')],
  },
  checklist: { 'application/octet-stream': ['pddata'] },
};

const getProps = (_, props) => props;

const getSecondProps = (_, __, props) => props;

/** @return {import("./reducer").AppReducer} */
const getState = state => state.app;

export const isLocationUpdating = createSelector(getState, state => state.location.updating);

export const locationHasChanged = createSelector(getState, state => state.location.hasChanged);

export const isLocationChangeBlocked = createSelector(
  getState,
  state => Object.keys(state.location.blocks).length > 0,
);

export const getBrowserIsNotificationCompatible = createSelector(
  getState,
  ({ isNotificationCompatible }) => isNotificationCompatible ?? false
);

export const getScrollSelector = createSelector(getState, state => state.scrollTo);

export const getPath = createSelector(getProps, path => {
  /** @type {string} */
  const strPath = Array.isArray(path) ? path.join('.') : path;
  return strPath;
});

/** App config selectors  */
const getAppConfig = createSelector(getState, state => state.config);

export const getCharLimit = createSelector(
  getAppConfig,
  getProps,
  (config, channel) => config?.conversation?.message?.[channel]?.maxLength ?? MAX_DEFAULT_MESSAGE_LENGTH
);

export const getConfigDealer = createSelector(getAppConfig, config => config?.dealer);

export const getIntegrations = createSelector(getConfigDealer, dealer => dealer?.config?.integration);

export const getSteps = createSelector(getConfigDealer, dealer => dealer?.config?.steps);

export const getStepsPlaceholders = createSelector(getSteps, steps => steps?.placeHolders);

export const getIntegrationDetail = createSelector(
  getIntegrations,
  getProps,
  (config, integration) => config?.[integration]
);

export const getEmailAllowedReactivations = createSelector(
  getAppConfig,
  config => config?.users?.email?.allowedReactivations
);

const getAvailableFilesConfigurationState = createSelector(getAppConfig, config => config.media);

export const getAvailableFilesConfiguration = createSelector(
  getAvailableFilesConfigurationState,
  getPath,
  /** @param {string} strPath */
  (state, strPath) => {
    /** @type {import('./types').AvailableNodeFileConfiguration} */
    const toReturn = getIn(state, strPath);
    return toReturn;
  }
);

export const getAvailableMediaTypes = createSelector(getAvailableFilesConfiguration, (configuration = {}) =>
  Object.keys(configuration)
    .filter(mediaType => LEGACY_MEDIA_TYPE.includes(mediaType.toUpperCase()))
    .map(mediaType => mediaType.toUpperCase()));

export const getMediaTypes = createSelector(getSecondProps, getAvailableMediaTypes, (mediaType, allMediaTypes = []) => {
  if (mediaType) {
    return Array.isArray(mediaType) ? mediaType : [mediaType];
  }
  return allMediaTypes;
});

export const getSupportedTypes = createSelector(
  getAvailableFilesConfiguration,
  getMediaTypes,
  (config, mediaTypes = []) =>
    uniq(
      mediaTypes.reduce(
        (acc, mediaType) => [
          ...acc,
          ...(config?.[mediaType.toLowerCase()]?.['supported-types']?.split(',')?.map(ft => ft.trim()) ?? []),
        ],
        []
      )
    )
);

export const getAllAvailableExtensions = createSelector(getPath, getSupportedTypes, (path, supportedTypes) =>
  supportedTypes
    ?.flatMap(type => {
      const toReturn =
        getIn(OVERRIDE_EXTENSIONS, `${path}.${type}`) ??
        OVERRIDE_EXTENSIONS.GLOBAL[type] ??
        mime.extensions[type] ??
        [];
      return toReturn;
    })
    ?.filter(Boolean));

export const getMediaMaxFiles = createSelector(
  getAvailableFilesConfiguration,
  getMediaTypes,
  (scoped, mediaTypes) =>
    mediaTypes.map(mediaType => scoped[mediaType.toLowerCase()]?.['max-files']).find(Boolean)
);

export const getMediaMaxFileSize = createSelector(
  getAvailableFilesConfiguration,
  getMediaTypes,
  (scoped, mediaTypes) =>
    mediaTypes.reduce((acc, mediaType) => {
      const maxFileSize = scoped?.[mediaType.toLowerCase()]?.['max-file-size'];
      if (maxFileSize) {
        return {
          ...acc,
          [mediaType]: maxFileSize,
        };
      }
      return acc;
    }, [])

);

export const getMediaMaxDuration = createSelector(
  getAvailableFilesConfiguration,
  getMediaTypes,
  (scoped, mediaTypes) =>
    mediaTypes.reduce((acc, mediaType) => {
      const maxDuration = scoped?.[mediaType.toLowerCase()]?.['max-length'];
      if (maxDuration) {
        return {
          ...acc,
          [mediaType]: maxDuration,
        };
      }
      return acc;
    }, {})
);

// Tires config
const getTireConfig = createSelector(getAppConfig, config => config.tire);

export const getTirePositions = createSelector(getTireConfig, ({ positions }) => positions);

export const getTireSpeedIndexes = createSelector(getTireConfig, ({ speedIndexes }) => speedIndexes);

export const getTireTypes = createSelector(getTireConfig, ({ types }) => types);

export const getFormattedTireTypes = createSelector(
  getTireTypes,
  getProps,
  /**
   * @param {Array<import('constants/InterventionConstants').TireTypeEnum>} types
   * @param {import('react-intl').IntlShape} intl
   * @returns {Array<{value: import('constants/InterventionConstants').TireTypeEnum, label: string}>}
   */
  (types, intl) => {
    const sortedTypes = [...types].sort((a, b) => {
      if (a === TireSeasonEnum.UNSPECIFIED) {
        return -1;
      }
      if (b === TireSeasonEnum.UNSPECIFIED) {
        return 1;
      }
      return a.localeCompare(b);
    });

    const options = sortedTypes.map(type => ({
      value: type,
      label: intl.formatMessage({ id: `tireType.${type}`, defaultMessage: humanize(type.toLowerCase()) }),
    }));

    return options;
  }
);

export const applicationIsStarted = createSelector(getState, state => state.isStarted);

export const applicationIsReady = createSelector(getState, state => state.isReady);

export const getHealthCheck = createSelector(getState, ({ healthCheck }) => healthCheck ?? {});

export const showHealthBanner = createSelector(getHealthCheck, ({ status }) => status && status !== HEALTH_STATUS.HEALTHY);

const getInitialCalls = createSelector(getState, state => state.initialCalls);

export const isAppLoading = createSelector(getInitialCalls, ({ isLoading }) => isLoading);

export const hasAppError = createSelector(getInitialCalls, ({ hasError }) => Boolean(hasError));

export const isConcurrencyModalOpen = createSelector(getState, state => state.concurrency.open);

export const getFuelTypes = createSelector(getState, ({ fuelTypes }) => fuelTypes);

const getThemeState = createSelector(getState, ({ themes }) => themes);

export const getFormattedThemes = createSelector(getThemeState, (themes) => themes.map(theme => ({
  value: theme,
  label: THEME_LABEL[theme] ?? ucFirst(humanize(theme)),
})) ?? []);

export const getSelfServiceWorkflow = createSelector(getState, ({ selfServiceWorkflow }) => selfServiceWorkflow);

export const getSelfServiceWorkflowByType = createSelector(getSelfServiceWorkflow, getProps, (selfServiceWorkflow, type) => selfServiceWorkflow[type]);

export const getFinalInvoiceConfig = createSelector(getAppConfig, config => config['final-invoice']);
